import React, { useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import { openDealerGuestAccount } from "../../../services/dealerGarage";
import { errorMessage } from "./dealerAdmin";

/**
 * Whose account a lead or inspection runs on. A customer who never signed up
 * can have a guest account opened for them, so their deposit, invoices and
 * purchase are tracked like anyone's; it gets no emails (only replies staff
 * write), and signing up with that email takes it over with everything on it.
 * Recording a deposit, an invoice or a sale opens one by itself - this is for
 * opening it ahead of that, or with a corrected name or phone number.
 *
 * Props: target ({ inspectionId } | { inquiryId }), record (the lead or
 *        inspection: email, names, phone), account ({ _id, isGuest } | null),
 *        onOpened(account)
 */
const GuestAccountBox = ({ target, record, account, onOpened }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  if (account) {
    if (!account.isGuest) return null;
    return (
      <div className={styles.inlineRow}>
        <span className={`${styles.chip} ${styles.chipMuted}`}>Guest account</span>
        <span className={styles.hint}>
          Tracked like any account, but no emails go to them except your replies - until they sign up with {record.email}.
        </span>
      </div>
    );
  }

  const open = () => setForm({ firstName: record.firstName || "", lastName: record.lastName || "", phone: record.phone || "" });

  const submit = async () => {
    setBusy(true);
    try {
      const res = await openDealerGuestAccount({
        ...target,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
      });
      if (res?.data && res.data.account) {
        setForm(null);
        onOpened(res.data.account);
      } else {
        alert(errorMessage(res, "Could not open the guest account."));
      }
    } catch (err) {
      alert("Could not open the guest account.");
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return (
      <div className={styles.inlineRow}>
        <span className={`${styles.chip} ${styles.chipMuted}`}>No account</span>
        <button type="button" className={styles.btnText} onClick={open}>Open a guest account</button>
        <span className={styles.hint}>Opened by itself when you record a deposit, invoice or sale for them.</span>
      </div>
    );
  }
  return (
    <div className={styles.subForm}>
      <div className={styles.grid3}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="guest-first">First name</label>
          <input id="guest-first" className={classes.formInput} value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="guest-last">Last name</label>
          <input id="guest-last" className={classes.formInput} value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="guest-phone">Phone</label>
          <input id="guest-phone" className={classes.formInput} value={form.phone} placeholder="e.g. 1 703 249 9922" onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
        </div>
      </div>
      <p className={styles.hint}>
        Under {record.email}. Nobody can sign in to it and it gets no emails except your replies; when they sign up with
        that email, it becomes theirs with everything on it.
      </p>
      <div className={classes.formButtons} style={{ alignItems: "center" }}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={submit}>
          {busy ? "Opening..." : "Open guest account"}
        </button>
        <button type="button" className={styles.btnText} disabled={busy} onClick={() => setForm(null)}>Cancel</button>
      </div>
    </div>
  );
};

export default GuestAccountBox;
